import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { eventBus } from '@/events/eventBus';
import { seedNow } from '@/domain/seedContext';
import type { NoDepositDeclineFlag } from '@/domain/types';

function nowIso(): string {
  return seedNow() ?? useSimulationStore.getState().clockIso;
}

export interface FlagNoDepositDeclineInput {
  customerId: string;
  /** The booking / override the decline was raised against, if any. */
  sourceBookingId?: string;
  /** Ops's typed decline reason (optional but usually present). */
  reason?: string;
  /** Persona who declined (Ops role). */
  by: string;
}

/**
 * Track A2 write (tv-bvx, diagrams 05/09): Ops declining a no-deposit override sets
 * the customer's decline flag. The agent can then no longer create a no-money booking
 * for them (no second trip through Ops) and must chase an actual deposit.
 *
 * STATE ONLY: the Ops decline card that fires this is a separate track (tv-o1f); the
 * agent queue that reads the flag is another (tv-b26 / Track C2). Idempotent per
 * customer (id = `ndf-${customerId}`); re-declining refreshes reason/by/createdAt and
 * clears any stale resolvedAt, so a fresh decline is active again.
 */
export function flagNoDepositDecline(input: FlagNoDepositDeclineInput): NoDepositDeclineFlag {
  const brain = useBrainStore.getState();
  const at = nowIso();
  const flag: NoDepositDeclineFlag = {
    id: `ndf-${input.customerId}`,
    customerId: input.customerId,
    sourceBookingId: input.sourceBookingId,
    reason: input.reason,
    by: input.by,
    createdAt: at,
    // resolvedAt intentionally unset: a fresh decline is active, and a re-decline
    // after a resolve drops the stale resolvedAt by rebuilding the flag here.
  };
  brain.upsertNoDepositDeclineFlag(flag);

  brain.appendActivity({
    // Reuse the closest existing ActivityType: an Ops decline resolves the
    // no-deposit override request. (No new union member so the exhaustive
    // ActivityFeedItem icon map stays untouched.)
    id: `act-nodeposit-decline-${input.customerId}`,
    type: 'override-resolved',
    customerId: input.customerId,
    body: `No-deposit override declined by ${input.by}${
      input.reason ? ` · reason: ${input.reason}` : ''
    }. No second trip through Ops; chase a deposit or cancel.`,
    timestamp: at,
  });

  eventBus.publish('no-deposit:declined', {
    customerId: input.customerId,
    flagId: flag.id,
    sourceBookingId: input.sourceBookingId,
  });

  return flag;
}

/**
 * Track C2 read (tv-b26): the booking forms and the agent queue call this to block a
 * no-money booking and surface the "was declined" alert. Pure read; returns the
 * customer's flag ONLY while it still blocks: unresolved, and no deposit paid for
 * the customer since it was set (B3: the same rule the server enforces, so a deposit
 * paid on any path lifts it even before a pay path stamps resolvedAt).
 */
export function activeDeclineFlagForCustomer(customerId: string): NoDepositDeclineFlag | undefined {
  const brain = useBrainStore.getState();
  const flag = brain.noDepositDeclineFlags[`ndf-${customerId}`];
  if (!flag || flag.resolvedAt) return undefined;
  const since = new Date(flag.createdAt).getTime();
  const paidSince = Object.values(brain.deposits).some(
    (d) => d.customerId === customerId && !!d.paidAt && new Date(d.paidAt).getTime() > since,
  );
  return paidSince ? undefined : flag;
}

/**
 * Track C2 resolve (tv-b26): the deposit-paid path stamps resolvedAt so the agent's
 * no-money block is lifted. No-op (returns undefined) when there is no active flag for
 * the customer - already resolved, or never declined.
 */
export function resolveNoDepositDecline(customerId: string): NoDepositDeclineFlag | undefined {
  const active = activeDeclineFlagForCustomer(customerId);
  if (!active) return undefined;

  const brain = useBrainStore.getState();
  const at = nowIso();
  const resolved: NoDepositDeclineFlag = { ...active, resolvedAt: at };
  brain.upsertNoDepositDeclineFlag(resolved);

  brain.appendActivity({
    id: `act-nodeposit-decline-resolved-${customerId}`,
    type: 'deposit-paid',
    customerId,
    body: 'Deposit paid; no-deposit decline flag cleared.',
    timestamp: at,
  });

  eventBus.publish('no-deposit:decline-resolved', {
    customerId,
    flagId: resolved.id,
  });

  return resolved;
}
